import { useMemo } from 'react';
import { useCurrentRefinements, useHits } from 'components/commercetools-ui/organisms/product/product-list-ssr/local-store';
import BooleanFacet from '../components/facets/boolean';
import ColorFacet from '../components/facets/color';
import RangeFacet from '../components/facets/range';
import TermFacet from '../components/facets/term';
import { FacetProps } from '../components/facets/types';
import { FacetConfiguration } from '../types';
import usePath from 'helpers/hooks/usePath';


/**
 * Facet ORDER on this SSR path is the order of the CMS / Frontastic Studio facet configuration —
 * `Object.keys(configuration)`. There is deliberately no `ordering` option: Algolia's
 * `renderingContent.facetOrdering` is no longer requested or cached for the server-preloaded path
 * (see helpers/algolia/plp-dataset-core.ts), so the configuration is the single source of truth for
 * which facets show and in what sequence. The browser-driven copy of this hook
 * (product-list-algolia/hooks/useDynamicFacets) still honours Algolia's ordering.
 */
interface Options {
  configuration?: Record<string, FacetConfiguration>;
  render?: (result: { attribute: string; Component: JSX.Element }) => JSX.Element;
}

const useDynamicFacets = ({ configuration, render }: Options = {}) => {
  const path = usePath();
  const { results } = useHits();
  const { items } = useCurrentRefinements();
  const visibleItemsCount = items.length > 0 ? 9999 : 8; 

  const facetMapping = useMemo<Record<FacetConfiguration['type'], React.ComponentType<FacetProps>>>(
    () => ({
      range: RangeFacet,
      color: ColorFacet,
      term: TermFacet,
      boolean: BooleanFacet,
    }),
    [],
  );

  const dynamicFacets = useMemo(() => {
    if (!configuration) return <></>;

    const facets = Object.keys(configuration).map((attribute) => {
      const facet = configuration[attribute];
      const isEmptyFacet =
        (facet.type === 'color' || facet.type === 'term' || facet.type === 'boolean' || facet.type==='range') &&
        !results?.disjunctiveFacets?.find((f) => f.name === attribute);
      const Component = facetMapping[facet.type];
      const FinalComponent = <Component key={attribute} label={facet.label} attribute={attribute} />;

      return {
        attribute,
        Component: (
          <div key={attribute} className={isEmptyFacet ? 'hidden' : 'block'}>
            {render?.({ attribute, Component: FinalComponent }) ?? FinalComponent}
          </div>
        ),
      };
    });

    //return facets.map((facet) => facet.Component);
    return (path.path.includes("clark") || path.path.includes("dorf"))  ? facets.slice(0, visibleItemsCount).map((facet) => facet?.Component) : facets.map((facet) => facet?.Component)

  }, [facetMapping, configuration, render, results?.disjunctiveFacets]);

  return dynamicFacets;
};

export default useDynamicFacets;
